import { TableToolbar, TableFilter, TableSortSelect, SortableHeader } from '../components/TableControls'
import { matchesTableSearch, sortTableRows, useTableSort, type TableSort } from '../lib/tableControls'
import { activityDateRange } from '../lib/agentActivity'
import { OnboardingReturn } from '../components/OnboardingFlow'
import { displayWorkSummary } from '../../supabase/functions/_shared/workSummary'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { CalendarDays, ChevronLeft, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react'

import { supabase, signOut, getCurrentSession } from '../lib/supabase'
import { useAuth, useTeamAccess } from '../contexts/AuthContext'
import { fetchAgentActivityStatus, type AgentActivityStatus } from '../lib/agentUsage'
import { connectorDateRange } from '../lib/connectorDateRange'
import { activityLabel } from '../../supabase/functions/_shared/activityDetails'
import { ActivitySummaryStatus } from '../components/ActivitySummaryStatus'
import { AgentStaleBadge } from '../components/AgentStaleBadge'
import { AppSidebar } from '../components/AppSidebar'
import { TopBar } from '../components/TopBar'
import { AgentConnectGrid } from '../components/AgentConnectGrid'
import { ConnectAgentModal } from '../components/ConnectAgentModal'
import type { AgentToolDef } from '../lib/agentTools'
import { SidebarInset, SidebarProvider } from '../components/ui/sidebar'
import { Skeleton } from '../components/ui/skeleton'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog'
import { buttonVariants } from '../components/ui/button'

// ============================================================================
// Page: /dashboard/team/:teamId/connectors
//
// Redesigned 2026-09-05 to match the Figma "Oppero agents" card grid, and
// merged with the old "Onboard Agent" sidebar item (see AppSidebar.tsx) —
// one "Agents" nav entry now covers both viewing and adding agents.
// Clarifying decisions made before building this:
//
// - Team: derived from the agent owner's pod (agents.user_id -> users.pod_id
//   -> pods.name), same convention as Check-ins/Summary — NOT an
//   independent field, so (unlike the Figma mock) one agent shows exactly
//   one team here.
// - Model doesn't exist in the `agents` table (id, user_id, team_id, name,
//   client_id, client_name, created_at/updated_at/deleted_at/paused_at — see
//   20250704221242_create_checkin_service.sql). Shown as an honest
//   display-only placeholder rather than the Figma's per-agent value: every
//   agent here really is a Claude Code auto check-in install, so "Model"
//   reads "Claude Code" for all of them.
//   Status (2026-10-03) is real: agents.paused_at (see
//   20261003090000_add_agent_pause_feature.sql, exposed through
//   agents_team_visible) drives Active/Paused per card. Pausing itself is
//   wired up on AgentDetail.tsx (owner-only); this grid only displays the
//   resulting state, it doesn't toggle it.
// - "Tools connected" (Slack/Figma in the Figma mock) was removed
//   2026-09-06 (explicit request) — there's no real tool-integration
//   concept in phase 1, so showing even the one honest "Slack" chip implied
//   more than actually exists.
// - Trash icon is wired to a real soft-delete (sets agents.deleted_at) via
//   the manage-agents edge function — needed because the per-agent
//   `agents_update`/`agents_delete` RLS policies are owner-only
//   (private.owns_user), and this is a team-wide view where an admin or
//   manager should be able to remove a teammate's agent too.
//
//   Extended 2026-09-29 for the three-tier permission model: admins can
//   delete any agent on the team; managers can delete an agent only if its
//   owner belongs to a pod they manage (checked both here, for which button
//   renders, and again server-side in manage-agents/index.ts, which is the
//   actual boundary — the client check is just so a manager doesn't see a
//   trash icon for agents they can't touch). Plain members still can't
//   delete anyone else's agent.
// - "+ Add Agent" opens a right-side Sheet (2026-10-03). Originally rendered
//   AgentInstallManager's `compact` mode directly; redone 2026-10-08 to
//   render AgentConnectGrid -- the same terminal/desktop tool-picker grid
//   WelcomeModal's onboarding step 6 already shipped (2026-09-09) -- instead
//   of a page-specific one, so the two entry points stay visually and
//   behaviorally identical rather than drifting the way their underlying
//   tool catalogs already had (see src/lib/agentTools.ts's header comment).
//   Clicking "Connect" on the one available tile (Claude Code) opens
//   ConnectAgentModal, the same component the onboarding wizard uses --
//   not AgentInstallManager, whose `compact` mode is now unused here (it's
//   still the non-compact manage view at /install/claude-code, untouched).
//   Every other tile (Codex, Cursor, ChatGPT) is a disabled "Coming soon"
//   placeholder per AGENT_TOOLS[].available, same as onboarding. UNLIKE
//   onboarding, "Connected" here is scoped to the caller's own agents/tokens,
//   not team-wide -- see connectedClientNames' own comment for the bug this
//   fixed (team-wide matching blocked a member from adding their own agent
//   whenever anyone else on the team already had one).
//   Deliberately NOT an Owner/Team/Channel provisioning form like a generic
//   "add X" mockup might show: an agent is self-installed (you run the hook
//   on your own machine), there's no "assign to someone else" path, no
//   Slack-channel concept on agents at all, and Team already auto-derives
//   from the owner's pod rather than being a pickable field (see the Team
//   bullet below) -- building those would be new backend work, not a
//   restyle, so this stays scoped to presentation only per explicit
//   instruction. Open to every member, not just admins (2026-09-06,
//   explicit decision) — an agent install is personal (agents.user_id =
//   whoever ran it), so any member should be able to add their own. Only
//   removal stays admin-gated.
// - Each agent can now be given a personal name at install time (see
//   AgentInstallManager.tsx's new "Name this agent" field / mint-install-
//   token's agent_name param) — before this, agents.name was always the
//   hardcoded tool name ("Claude Code"), making every card indistinguishable
//   from every other on this exact grid.
// - A member can rename their own agent too (2026-09-06). Originally
//   "only the owner," direct-client RLS update, no edge function. Extended
//   2026-10-03 (explicit request, to match AgentDetail.tsx and fix the
//   asymmetry between the two pages) so an admin, or a manager of the
//   agent's owner's pod, can rename it too -- same rule canDelete already
//   used for the trash icon. A non-owner's direct RLS update would silently
//   no-op (agents_update requires owns_user), so this now always goes
//   through manage-agents' 'rename' action (owner OR admin OR
//   manager-of-pod, enforced there -- this page's isMine/canDelete gate only
//   decides what renders). Renders as a Dialog (not window.prompt) matching
//   the Input + label + Cancel/primary-Button footer convention already used
//   by Team Management's Create Team panel.
// - Company-configurable allow-list of agent *types* (e.g. "only Claude
//   Code, Claude Desktop, Cowork") lives on the Config page's "Allow agents"
//   tab (AllowedAgentsConfig.tsx, 2026-09-06) -- note its catalog
//   (claude_code/claude_desktop/cowork ids) still doesn't match this page's
//   AgentConnectGrid catalog (claude-code/codex/cursor/chatgpt ids, from
//   src/lib/agentTools.ts); reconciling the two is a separate, not-yet-done
//   follow-up. Nothing enforces that allow-list yet either way -- mint-
//   install-token and this page's Connect flow don't check
//   enabled_agent_types, so today every member can still add a Claude Code
//   agent regardless of its toggle.
// - The token-spend leaderboard (AgentsSection/AgentsCostPanel) that used
//   to live on this page was removed 2026-09-06 (explicit request) — this
//   page is agent cards only now. AgentsCostPanel.tsx itself is untouched
//   in case it's wanted somewhere else later.
// - 2026-10-02 (Step 6 of docs/TOKEN_COST_ACCURACY_PLAN.md): each card shows
//   a "No activity in 24h" badge when nothing (usage event or check-in) has
//   arrived from that agent in 24h, so a dead hook is visible. See
//   fetchAgentActivityStatus in src/lib/agentUsage.ts for how the
//   timestamp is resolved per role.
// ============================================================================

interface TeamAgentsProps {
  teamId: string
}

interface AgentRow {
  id: string
  name: string
  user_id: string
  created_at: string
  paused_at: string | null
  // Added 2026-10-08 so the "+ Add Agent" sheet's AgentConnectGrid can mark
  // a tool "Connected" from data this page already loads, instead of a
  // second poll -- agents_team_visible already exposes this column (see
  // 20261003090000_add_agent_pause_feature.sql), this page just wasn't
  // selecting it before.
  client_name: string | null
}

interface Member {
  id: string
  name: string | null
  email: string | null
  avatar_url: string | null
  pod_id: string | null
}

interface Pod {
  id: string
  name: string
}

interface AgentActivitySummaryRow {
  agent_id: string
  agent_name: string | null
  owner_name: string | null
  owner_email: string | null
  pod_name: string | null
  latest_checkin_date: string | null
  latest_task: string | null
  latest_created_at: string | null
  activity_count: number
  files_changed: number
  latest_activity_kind: 'activity' | 'completed_work' | null
  latest_classification?: string | null
  latest_summary_status?: 'ready' | 'pending' | 'unavailable' | null
  latest_summary_attempts?: number | null
  latest_summary_last_attempt_at?: string | null
  latest_summary_error?: string | null
  total_count: number
}

type ActivityRange = '7' | '10' | '30' | 'all' | 'custom'
const MIN_ACTIVITY_PAGE_SIZE = 8
const MAX_ACTIVITY_PAGE_SIZE = 20

function activityPageSizeForHeight(height: number): number {
  // Reserve space for the app chrome, page heading, tabs, filters, and
  // pagination. Rows keep their normal Team Management spacing; only the
  // number requested from the server changes with the viewport.
  return Math.max(MIN_ACTIVITY_PAGE_SIZE, Math.min(MAX_ACTIVITY_PAGE_SIZE, Math.floor((height - 400) / 60)))
}

const AVATAR_PALETTE = [
  'bg-sky-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-fuchsia-500',
  'bg-cyan-500',
  'bg-violet-500',
  'bg-emerald-500',
]

function paletteFor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]
}

function initialsOf(name: string): string {
  const initials = name
    .split(/[\s-]+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
  return initials || '?'
}

function displayName(member: Pick<Member, 'name' | 'email'>): string {
  return member.name || member.email?.split('@')[0] || 'Unknown user'
}

const TeamAgents: React.FC<TeamAgentsProps> = ({ teamId }) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const focusUserId = searchParams.get('user')
  const { role } = useTeamAccess(teamId)
  const { userTeams, user } = useAuth()
  const isAdmin = role === 'admin' || role === 'owner'
  const isManager = role === 'manager'
  // This team's public users.id for the signed-in viewer, so we can tell
  // "is this card mine" without an extra query — team_members already
  // carries user_id alongside team_id in userTeams.
  const myUserId = userTeams?.find((tm) => tm.team_id === teamId)?.user_id

  const [teamName, setTeamName] = useState<string | undefined>(undefined)
  const [teamTimezone, setTeamTimezone] = useState('UTC')
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [pods, setPods] = useState<Pod[]>([])
  // pod ids the signed-in viewer manages, only fetched/used when role is
  // 'manager' — drives which agents' trash icons render for them. Admins
  // don't need this (they can delete anything); members never see the icon.
  const [managedPodIds, setManagedPodIds] = useState<Set<string>>(new Set())
  const [activityByAgent, setActivityByAgent] = useState<Map<string, AgentActivityStatus>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AgentRow | null>(null)
  const [renameTarget, setRenameTarget] = useState<AgentRow | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [renaming, setRenaming] = useState(false)
  const [addAgentOpen, setAddAgentOpen] = useState(() => searchParams.get('open') === 'add')
  // Which AgentConnectGrid tile is mid-connect -- only ever 'claude-code'
  // today since every other tool in AGENT_TOOLS is `available: false` (its
  // Connect button never fires this). Same id type ConnectAgentModal and
  // WelcomeModal's step 6 already use.
  const [connectingTool, setConnectingTool] = useState<AgentToolDef['id'] | null>(null)
  const [activeView, setActiveView] = useState<'agents' | 'activity'>(() => searchParams.get('view') === 'activity' ? 'activity' : 'agents')
  const customActivityRange = activityDateRange(searchParams.get('from'), searchParams.get('to'))
  const [activityRows, setActivityRows] = useState<AgentActivitySummaryRow[]>([])
  const [activityLoading, setActivityLoading] = useState(false)
  const [activityError, setActivityError] = useState<string | null>(null)
  const [activityRange, setActivityRange] = useState<ActivityRange>(() => {
    if (customActivityRange) return 'custom'
    const value = searchParams.get('range')
    return value === '10' || value === '30' || value === 'all' ? value : '7'
  })
  const [activityOwnerId, setActivityOwnerId] = useState(() => searchParams.get('owner') ?? 'all')
  const [connectorSearch, setConnectorSearch] = useState('')
  const [connectorStatus, setConnectorStatus] = useState('all')
  const [connectorOwner, setConnectorOwner] = useState('all')
  const [connectorTool, setConnectorTool] = useState('all')
  const [connectorTeam, setConnectorTeam] = useState('all')
  const { sort: connectorSort, setSort: setConnectorSort } = useTableSort('name')
  const [activitySearch, setActivitySearch] = useState(() => searchParams.get('search') || '')
  const [activityQuery, setActivityQuery] = useState(activitySearch)
  const { sort: activitySort, toggleSort: toggleActivity } = useTableSort('latest', 'desc')
  const sortActivity = (key: string) => { toggleActivity(key); setActivityPage(0) }
  useEffect(() => {
    const timer = setTimeout(() => { setActivityQuery(activitySearch); setActivityPage(0) }, 250)
    return () => clearTimeout(timer)
  }, [activitySearch])
  const [activityPodId, setActivityPodId] = useState(() => searchParams.get('pod') ?? 'all')

  useEffect(() => {
    if (!focusUserId) return
    setActiveView('activity')
    setActivityOwnerId(focusUserId)
  }, [focusUserId, members])
  const [activityPage, setActivityPage] = useState(() => {
    const page = Number(searchParams.get('page'))
    return Number.isInteger(page) && page >= 0 ? page : 0
  })
  const [activityTotal, setActivityTotal] = useState(0)
  const [activityPageSize, setActivityPageSize] = useState(() =>
    activityPageSizeForHeight(typeof window === 'undefined' ? 900 : window.innerHeight)
  )
  // Claimed-but-not-yet-checked-in install tokens for this team -- fetched
  // only while the sheet is open, same 4s poll cadence ConnectAgentModal and
  // WelcomeModal's step 6 use for the identical "ran the command but hasn't
  // had a real check-in yet" gap. Combined with `agents` below so
  // AgentConnectGrid's "Connected" badge means the same thing here as it
  // does in onboarding and in ConnectAgentModal, instead of a narrower
  // "has a real agent row" definition unique to this page.
  const [claimedToolNames, setClaimedToolNames] = useState<Set<string>>(new Set())

  const updateActivityUrl = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(updates)) {
      if (!value) next.delete(key)
      else next.set(key, value)
    }
    setSearchParams(next, { replace: true })
  }

  const handleActivityOwnerChange = (value: string) => {
    setActivityOwnerId(value)
    setActivityPage(0)
    updateActivityUrl({ owner: value === 'all' ? null : value, search: null, page: null })
  }

  const handleActivityPodChange = (value: string) => {
    setActivityPodId(value)
    setActivityPage(0)
    updateActivityUrl({ pod: value === 'all' ? null : value, page: null })
  }

  const handleActivityRangeChange = (value: ActivityRange) => {
    setActivityRange(value)
    setActivityPage(0)
    updateActivityUrl({ range: value === '7' ? null : value, page: null, from: null, to: null })
  }

  const handleActivityPageChange = (value: number) => {
    setActivityPage(value)
    updateActivityUrl({ page: value === 0 ? null : String(value) })
  }

  const clearActivityFilters = () => {
    setActivityOwnerId('all')
    setActivityPodId('all')
    setActivityRange('7')
    setActivitySearch(''); setActivityQuery('')
    setActivityPage(0)
    updateActivityUrl({ owner: null, search: null, pod: null, range: null, page: null, user: null, from: null, to: null })
  }

  const loadAll = async () => {
    setLoading(true)
    setError(null)

    const [teamResult, agentsResult, membersResult, podsResult] = await Promise.all([
      supabase.from('teams').select('name, standup_config').eq('id', teamId).single(),
      (supabase.from as any)('agents_team_visible')
        .select('id, name, user_id, created_at, paused_at, client_name')
        .eq('team_id', teamId),
      (supabase.from as any)('users')
        .select('id, name, email, avatar_url, pod_id')
        .eq('team_id', teamId),
      (supabase.from as any)('pods').select('id, name').eq('team_id', teamId),
    ])

    if (teamResult.error || agentsResult.error || membersResult.error || podsResult.error) {
      console.error(
        'Failed to load Agents page data:',
        teamResult.error ?? agentsResult.error ?? membersResult.error ?? podsResult.error
      )
      setError('Could not load connectors for this team.')
      setLoading(false)
      return
    }

    setTeamName(teamResult.data?.name ?? undefined)
    setTeamTimezone((teamResult.data?.standup_config as { timezone?: string } | null)?.timezone || 'UTC')
    setAgents((agentsResult.data ?? []) as AgentRow[])
    setMembers((membersResult.data ?? []) as Member[])
    setPods((podsResult.data ?? []) as Pod[])
    setLoading(false)
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      await loadAll()
      if (cancelled) return
    })()
    return () => {
      cancelled = true
    }
  }, [teamId])

  // Separate from loadAll (and re-runs independently) because `role` and
  // `user` come from useTeamAccess/useAuth's own async resolution, which can
  // still be settling by the time loadAll's mount effect above fires -- this
  // re-fires once they're ready instead of capturing a stale isManager=false.
  useEffect(() => {
    let cancelled = false

    const loadManagedPods = async () => {
      if (!isManager || !user?.id || pods.length === 0) {
        setManagedPodIds(new Set())
        return
      }
      // pod_managers' own SELECT policy is permissive (true, not team-scoped
      // -- see 20260928201832_add_pod_managers_table.sql), so this query
      // scopes itself to this team's pod ids rather than relying on RLS to.
      const { data, error } = await (supabase.from as any)('pod_managers')
        .select('pod_id')
        .eq('auth_user_id', user.id)
        .in('pod_id', pods.map((p) => p.id))
      if (cancelled) return
      if (error) {
        console.error('Failed to load managed pods:', error)
        return
      }
      setManagedPodIds(new Set((data ?? []).map((r: { pod_id: string }) => r.pod_id)))
    }

    loadManagedPods()
    return () => {
      cancelled = true
    }
  }, [isManager, user?.id, pods])

  // Poll claimed-but-not-checked-in install tokens while the "+ Add Agent"
  // sheet is open -- see claimedToolNames above for why this exists
  // alongside `agents`. Scoped to the CALLER's own tokens (`user_id`,
  // NOT NULL on this table) -- see connectedClientNames' comment for why
  // this can't be team-wide the way WelcomeModal's identical poll is.
  useEffect(() => {
    if (!addAgentOpen || !myUserId) return
    let cancelled = false
    const refresh = async () => {
      const { data } = await (supabase.from as any)('hook_install_tokens')
        .select('client_name')
        .eq('team_id', teamId)
        .eq('user_id', myUserId)
        .not('claimed_at', 'is', null)
        .is('revoked_at', null)
      if (cancelled) return
      const names = new Set<string>()
      for (const row of (data ?? []) as { client_name: string | null }[]) {
        const name = row.client_name?.toLowerCase().trim()
        if (name) names.add(name)
      }
      setClaimedToolNames(names)
    }
    refresh()
    const interval = setInterval(refresh, 4000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [addAgentOpen, teamId, myUserId])

  // Union of the CALLER's own agent rows and their own claimed install
  // tokens above.
  //
  // 2026-10-08 bugfix: this used to match team-wide (any agent/token with
  // this client_name, regardless of owner) -- copied from WelcomeModal's
  // step 6, where "has the team connected a Claude Code agent yet" is the
  // right question for a one-time setup checklist. It's the wrong question
  // here: per this file's own header comment, adding an agent is a
  // *personal* action every member can do independently of what teammates
  // already connected. Team-wide matching meant one person's (or, on this
  // team, several pieces of seed/demo data's) live "Claude Code" agent
  // permanently showed "Connected" with no Connect button for every other
  // member, even one who'd never added their own and had just deleted
  // theirs. Scoping to `agent.user_id`/token `user_id` === the caller's own
  // internal user id (myUserId, already used by the isMine/canDelete gates
  // below) fixes that without touching WelcomeModal, where team-wide
  // remains correct.
  const connectedClientNames = useMemo(() => {
    const names = new Set(claimedToolNames)
    if (myUserId) {
      for (const agent of agents) {
        if (agent.user_id !== myUserId) continue
        const name = agent.client_name?.toLowerCase().trim()
        if (name) names.add(name)
      }
    }
    return names
  }, [agents, claimedToolNames, myUserId])

  // Dead-hook badges. Admins/managers can read the raw tables for an exact
  // last-activity time. The usage RPC only attributes usage to agents the
  // caller may see in detail, so non-admins get no badge (not a false one)
  // for agents they can't see -- see fetchAgentActivityStatus.
  const canReadRaw = isAdmin || isManager
  const agentIdsKey = agents.map((a) => a.id).join(',')
  useEffect(() => {
    if (agents.length === 0) {
      setActivityByAgent(new Map())
      return
    }
    let cancelled = false
    fetchAgentActivityStatus(
      teamId,
      agents.map((a) => ({ id: a.id, created_at: a.created_at })),
      { canReadRaw, canSeeAllAgents: isAdmin }
    )
      .then((map) => {
        if (!cancelled) setActivityByAgent(map)
      })
      .catch((err) => console.error('Failed to load agent activity status:', err))
    return () => {
      cancelled = true
    }
  }, [teamId, agentIdsKey, canReadRaw, isAdmin])

  useEffect(() => {
    const updatePageSize = () => {
      const nextSize = activityPageSizeForHeight(window.innerHeight)
      setActivityPageSize((currentSize) => {
        if (currentSize !== nextSize) setActivityPage(0)
        return nextSize
      })
    }
    updatePageSize()
    window.addEventListener('resize', updatePageSize)
    return () => window.removeEventListener('resize', updatePageSize)
  }, [])

  useEffect(() => {
    if (activeView !== 'activity' || agents.length === 0) return
    let cancelled = false
    setActivityLoading(true)
    setActivityError(null)
    const range = connectorDateRange(activityRange === 'all' || activityRange === 'custom' ? null : Number(activityRange), teamTimezone)

    ;(supabase.rpc as any)('get_team_agent_activity_summary_sorted', {
      p_team_id: teamId,
      p_from: activityRange === 'custom' && customActivityRange ? customActivityRange.from : range.from,
      p_to: activityRange === 'custom' && customActivityRange ? customActivityRange.to : range.to,
      p_pod_id: activityPodId === 'all' ? null : activityPodId,
      p_owner_id: activityOwnerId === 'all' ? null : activityOwnerId,
      p_search: activityQuery || null,
      p_sort: activitySort.key, p_direction: activitySort.direction,
      p_page_size: activityPageSize,
      p_page: activityPage,
    }).then(({ data, error }: { data: AgentActivitySummaryRow[] | null; error: unknown }) => {
      if (cancelled) return
      if (error) {
        console.error('Failed to load team agent activity:', error)
        setActivityError('Could not load connector activity for this team.')
      } else {
        const rows = data ?? []
        setActivityRows(rows)
        setActivityTotal(rows[0]?.total_count ?? 0)
      }
      setActivityLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [activeView, activityPage, activityPageSize, activityOwnerId, activityPodId, activityRange, activityQuery, activitySort, teamId, teamTimezone, agents.length, customActivityRange?.from, customActivityRange?.to])

  const goToDashboard = () => navigate(`/dashboard/team/${teamId}`)

  const handleLogout = async () => {
    await signOut()
    navigate('/auth/login')
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    const agent = deleteTarget
    setDeletingId(agent.id)
    try {
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')
      const { error: fnError } = await supabase.functions.invoke('manage-agents', {
        body: { action: 'delete', team_id: teamId, agent_id: agent.id },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (fnError) throw fnError
      toast.success('Connector removed')
      setAgents((prev) => prev.filter((a) => a.id !== agent.id))
      setDeleteTarget(null)
    } catch (err) {
      console.error('Failed to delete agent:', err)
      toast.error('Could not remove this connector')
    } finally {
      setDeletingId(null)
    }
  }

  const openRename = (agent: AgentRow) => {
    setRenameTarget(agent)
    setRenameValue(agent.name)
  }

  const handleRenameSubmit = async () => {
    if (!renameTarget) return
    const trimmed = renameValue.trim().slice(0, 60)
    if (!trimmed || trimmed === renameTarget.name) {
      setRenameTarget(null)
      return
    }

    setRenaming(true)
    try {
      // Routed through manage-agents (2026-10-03), not a direct client
      // update -- this control is now reachable by an admin/manager acting
      // on someone else's agent (see the isMine || canDelete gate on the
      // pencil icon), and agents_update RLS only allows the owner's own row,
      // so a non-owner's direct update would silently no-op rather than
      // error. The edge function's own authorization (owner OR admin OR
      // manager-of-pod) is the real boundary; this page's gate just decides
      // what renders.
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')
      const { error } = await supabase.functions.invoke('manage-agents', {
        body: { action: 'rename', team_id: teamId, agent_id: renameTarget.id, name: trimmed },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })

      if (error) throw error
      toast.success('Connector renamed')
      setAgents((prev) =>
        prev.map((a) => (a.id === renameTarget.id ? { ...a, name: trimmed } : a))
      )
      setRenameTarget(null)
    } catch (err) {
      console.error('Failed to rename agent:', err)
      toast.error('Could not rename this connector')
    } finally {
      setRenaming(false)
    }
  }

  const membersById = new Map(members.map((m) => [m.id, m]))
  const podsById = new Map(pods.map((p) => [p.id, p.name]))
  const activityOwners = useMemo(() => {
    const owners = new Map<string, Member>()
    for (const agent of agents) {
      const owner = membersById.get(agent.user_id)
      if (owner) owners.set(owner.id, owner)
    }
    return Array.from(owners.values()).sort((a, b) => displayName(a).localeCompare(displayName(b)))
  }, [agents, members])

  const visibleAgents = sortTableRows(agents.filter(agent => {
    const owner = membersById.get(agent.user_id)
    return matchesTableSearch(connectorSearch, agent.name, agent.client_name, owner?.name, owner?.email, podsById.get(owner?.pod_id || ''))
      && (connectorOwner === 'all' || agent.user_id === connectorOwner)
      && (connectorTool === 'all' || agent.client_name === connectorTool)
      && (connectorTeam === 'all' || (connectorTeam === 'unassigned' ? !owner?.pod_id : owner?.pod_id === connectorTeam))
      && (connectorStatus === 'all' || (connectorStatus === 'paused' ? !!agent.paused_at : !agent.paused_at))
  }), connectorSort, { name: agent => agent.name, owner: agent => membersById.get(agent.user_id)?.name || membersById.get(agent.user_id)?.email,
    created: agent => Date.parse(agent.created_at), tool: agent => agent.client_name })

  return (
    <SidebarProvider>
      <AppSidebar
        activeSection="agents"
        onNavigate={goToDashboard}
        onOpenTeamManagement={() => navigate(`/dashboard/team/${teamId}/team-management`)}
        onOpenBilling={() => navigate(`/dashboard/team/${teamId}?open=billing`)}
        teamId={teamId}
      />
      <SidebarInset>
        <TopBar teamId={teamId} teamName={teamName} onLogout={handleLogout} sectionLabel="Connectors" />
        <main className="flex min-h-0 flex-1 flex-col space-y-6 overflow-hidden bg-background p-4 sm:p-6">
          <OnboardingReturn teamId={teamId} />
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground mb-1">
                {activeView === 'activity' ? 'Connector activity' : teamName ? `${teamName} connectors` : 'Connectors'}
              </h1>
              <p className="text-muted-foreground text-sm">
                {activeView === 'activity' ? 'All automated work recorded across your team.' : 'Every Claude Code check-in connector across your team.'}
              </p>
            </div>
            {activeView === 'agents' && (
              <Button onClick={() => setAddAgentOpen(true)} className="gap-1.5">
                <Plus className="h-4 w-4" />
                Add Connector
              </Button>
            )}
          </div>

          <div className="flex gap-4 border-b border-border">
              <button type="button" onClick={() => setActiveView('agents')} className={`border-b-2 px-1 pb-2 text-sm font-medium ${activeView === 'agents' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>Connectors</button>
              <button type="button" onClick={() => setActiveView('activity')} className={`border-b-2 px-1 pb-2 text-sm font-medium ${activeView === 'activity' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>Connector Activity</button>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {!error && (
            <>
              {activeView === 'agents' && !loading && <TableToolbar label="connectors" query={connectorSearch} onQueryChange={setConnectorSearch} count={visibleAgents.length} total={agents.length}
            active={!!connectorSearch || connectorOwner !== 'all' || connectorStatus !== 'all' || connectorTool !== 'all' || connectorTeam !== 'all'} onClear={() => { setConnectorSearch(''); setConnectorOwner('all'); setConnectorStatus('all'); setConnectorTool('all'); setConnectorTeam('all') }}>
            <TableFilter label="Filter connector owner" value={connectorOwner} onChange={setConnectorOwner} allLabel="All owners" options={activityOwners.map(owner => ({ value: owner.id, label: displayName(owner) }))} />
            <TableFilter label="Filter connector status" value={connectorStatus} onChange={setConnectorStatus} allLabel="All statuses" options={[{ value: 'active', label: 'Active' }, { value: 'paused', label: 'Paused' }]} />
            <TableFilter label="Filter connector tool" value={connectorTool} onChange={setConnectorTool} allLabel="All tools" options={[...new Set(agents.map(agent => agent.client_name).filter((name): name is string => !!name))].sort().map(value => ({ value, label: value }))} />
            <TableFilter label="Filter connector team" value={connectorTeam} onChange={setConnectorTeam} allLabel="All teams" options={[{ value: 'unassigned', label: 'Unassigned' }, ...pods.map(pod => ({ value: pod.id, label: pod.name }))]} />
            <TableSortSelect sort={connectorSort} onChange={setConnectorSort} options={[{ value: 'name', label: 'Name' }, { value: 'owner', label: 'Owner' }, { value: 'created', label: 'Created' }, { value: 'tool', label: 'Tool' }]} />
          </TableToolbar>}
          {activeView === 'agents' && !loading && agents.length > 0 && visibleAgents.length === 0 && <p className="text-sm text-muted-foreground">No connectors match these filters.</p>}
          {activeView === 'activity' ? (
                <AgentActivityPanel
                  agents={agents}
                  membersById={membersById}
                  activityRows={activityRows}
                  loading={activityLoading}
                  error={activityError}
                  range={activityRange}
                  customRangeLabel={customActivityRange ? customActivityRange.from === customActivityRange.to ? customActivityRange.from : `${customActivityRange.from} – ${customActivityRange.to}` : undefined}
                  onRangeChange={handleActivityRangeChange}
                  ownerId={activityOwnerId}
                  owners={activityOwners}
                  onOwnerChange={handleActivityOwnerChange}
                  podId={activityPodId}
                  pods={pods}
                  onPodChange={handleActivityPodChange}
                  page={activityPage}
                  total={activityTotal}
                  pageSize={activityPageSize}
                  onPageChange={handleActivityPageChange}
                  onOpenAgent={(agentId) => navigate(`/dashboard/team/${teamId}/connectors/${agentId}`)}
                  focused={Boolean(focusUserId)}
                  isManager={isManager}
                  onClearFilters={clearActivityFilters}
                  query={activitySearch} onQueryChange={setActivitySearch} sort={activitySort} onSort={sortActivity}
                />
              ) : (
              loading ? (
                // Shape mirrors a loaded agent card (square avatar, name +
                // subtitle, badge row, team/owner footer) so the shimmer reads
                // as "cards incoming" rather than a generic bar.
                //
                // Plain div + bg-background, not the shadcn <Card> (bg-card),
                // throughout this grid (2026-10-03) -- bg-card is pure white
                // and reads as a glaringly white box against the cream
                // --background page tone (see src/index.css); every other
                // restyled page (TeamManagement.tsx, Summary, Check-ins) uses
                // this same bg-background convention, see UI_STYLE_GUIDE.md.
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="border border-border bg-background shadow-sm">
                      <div className="space-y-4 p-4 sm:p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <Skeleton className="h-10 w-10 shrink-0 rounded-md" />
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-28" />
                              <Skeleton className="h-3 w-20" />
                            </div>
                          </div>
                          <Skeleton className="h-6 w-16 shrink-0 rounded-full" />
                        </div>
                        <div className="grid grid-cols-2 gap-3 border-t border-border pt-3">
                          <div className="space-y-2">
                            <Skeleton className="h-3 w-10" />
                            <Skeleton className="h-4 w-16" />
                          </div>
                          <div className="space-y-2">
                            <Skeleton className="h-3 w-10" />
                            <Skeleton className="h-4 w-20" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : agents.length === 0 ? (
                <div className="border border-border bg-background shadow-sm">
                  <div className="flex flex-col items-center gap-3 p-8 text-center">
                    <p className="text-sm text-muted-foreground">
                      No connectors yet. Install the Claude Code auto check-in hook to add your first one.
                    </p>
                    <Button onClick={() => setAddAgentOpen(true)} className="gap-1.5">
                      <Plus className="h-4 w-4" />
                      Add Connector
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {visibleAgents.map((agent) => {
                    const owner = membersById.get(agent.user_id)
                    const ownerName = owner ? displayName(owner) : 'Unknown owner'
                    const teamLabel = owner?.pod_id ? podsById.get(owner.pod_id) ?? '—' : '—'
                    const isMine = !!myUserId && agent.user_id === myUserId
                    // Admins can delete any agent; managers only one whose
                    // owner is in a pod they manage (mirrors the real check
                    // in manage-agents/index.ts -- this just decides whether
                    // the icon renders, the edge function is the boundary).
                    const canDelete =
                      isAdmin || (isManager && !!owner?.pod_id && managedPodIds.has(owner.pod_id))

                    return (
                      <div
                        key={agent.id}
                        onClick={() => navigate(`/dashboard/team/${teamId}/connectors/${agent.id}`)}
                        className="cursor-pointer border border-border bg-background shadow-sm transition-colors hover:border-primary/40"
                      >
                        <div className="space-y-4 p-4 sm:p-5">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex min-w-0 items-center gap-3">
                              <Avatar className="h-10 w-10 shrink-0">
                                <AvatarFallback
                                  className={`text-sm font-semibold text-white ${paletteFor(agent.id)}`}
                                >
                                  {initialsOf(agent.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0">
                                <div className="truncate font-semibold text-foreground">
                                  {agent.name}
                                </div>
                                <div className="text-sm text-muted-foreground">Claude Code</div>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center justify-end gap-2">
                              <AgentStaleBadge status={activityByAgent.get(agent.id)} />
                              {agent.paused_at ? (
                                <Badge variant="secondary" className="font-medium">
                                  Paused
                                </Badge>
                              ) : (
                                <Badge variant="success" className="font-medium">
                                  Active
                                </Badge>
                              )}
                              {(isMine || canDelete) && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    openRename(agent)
                                  }}
                                  aria-label={`Rename ${agent.name}`}
                                  className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted disabled:opacity-50"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setDeleteTarget(agent)
                                  }}
                                  disabled={deletingId === agent.id}
                                  aria-label={`Remove ${agent.name}`}
                                  className="flex h-8 w-8 items-center justify-center rounded-md text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3 border-t border-border pt-3 text-sm">
                            <div>
                              <div className="text-xs text-muted-foreground">Team</div>
                              <div className="font-medium text-foreground">{teamLabel}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted-foreground">Owner</div>
                              <div className="flex items-center gap-1.5 font-medium text-foreground">
                                <Avatar className="h-5 w-5">
                                  {owner?.avatar_url && <AvatarImage src={owner.avatar_url} alt="" />}
                                  <AvatarFallback className="text-[10px]">
                                    {initialsOf(ownerName)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="truncate">{ownerName}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
              )}
            </>
          )}
        </main>
      </SidebarInset>

      <Dialog
        open={!!renameTarget}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename connector</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <label className="text-sm font-medium text-foreground">Connector name</label>
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameSubmit()
              }}
              maxLength={60}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleRenameSubmit}
              disabled={renaming || !renameValue.trim()}
            >
              {renaming ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This stops its check-in reminders.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: 'destructive' })}
              disabled={!!deletingId}
              onClick={(e) => {
                e.preventDefault()
                handleConfirmDelete()
              }}
            >
              {deletingId ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={addAgentOpen} onOpenChange={setAddAgentOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Add connector</SheetTitle>
            <SheetDescription>
              Pick a tool below to connect it to your account.
            </SheetDescription>
          </SheetHeader>
          <div className="py-4">
            <AgentConnectGrid
              connectedClientNames={connectedClientNames}
              onConnect={(tool) => setConnectingTool(tool.id)}
            />
          </div>
        </SheetContent>
      </Sheet>

      <ConnectAgentModal
        isOpen={connectingTool === 'claude-code'}
        onClose={() => setConnectingTool(null)}
        teamId={teamId}
        onConnected={() => {
          setClaimedToolNames((prev) => new Set(prev).add('claude code'))
          loadAll()
        }}
      />
    </SidebarProvider>
  )
}

interface AgentActivityPanelProps {
  agents: AgentRow[]
  membersById: Map<string, Member>
  ownerId: string
  owners: Member[]
  activityRows: AgentActivitySummaryRow[]
  loading: boolean
  error: string | null
  range: ActivityRange
  customRangeLabel?: string
  onRangeChange: (range: ActivityRange) => void
  onOwnerChange: (value: string) => void
  podId: string
  pods: Pod[]
  onPodChange: (value: string) => void
  page: number
  total: number
  pageSize: number
  onPageChange: (page: number) => void
  onOpenAgent: (agentId: string) => void
  focused: boolean
  isManager: boolean
  onClearFilters: () => void
  query: string; onQueryChange: (query: string) => void; sort: TableSort; onSort: (key: string) => void
}

const AgentActivityPanel: React.FC<AgentActivityPanelProps> = ({
  agents,
  membersById,
  ownerId,
  owners,
  activityRows,
  loading,
  error,
  range,
  customRangeLabel,
  onRangeChange,
  onOwnerChange,
  podId,
  pods,
  onPodChange,
  page,
  total,
  pageSize,
  onPageChange,
  onOpenAgent,
  focused,
  isManager,
  onClearFilters, query, onQueryChange, sort, onSort,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const hasFilters = Boolean(query || ownerId !== 'all' || podId !== 'all' || range !== '7')

  return (
  <div className={focused ? 'flex flex-col gap-4' : 'flex min-h-0 flex-1 flex-col gap-4'}>
      <div className="flex shrink-0 flex-col gap-3 border border-border bg-background p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-foreground">{focused ? 'Focused connector activity' : 'Team-wide connector activity'}</p>
          <p className="mt-1 text-sm text-muted-foreground">{focused ? 'Showing the activity related to this notification.' : 'Review automated work across all connected connectors.'}</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Select value={ownerId} onValueChange={onOwnerChange}>
            <SelectTrigger aria-label="Filter connector activity by owner" className="h-9 w-full sm:w-52"><SelectValue placeholder="All owners" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All owners</SelectItem>
              {owners.map((owner) => (
                <SelectItem key={owner.id} value={owner.id}>
                  <span className="flex items-center gap-2">
                    <Avatar className="h-5 w-5">
                      {owner.avatar_url && <AvatarImage src={owner.avatar_url} alt="" />}
                      <AvatarFallback className={`${paletteFor(displayName(owner))} text-[9px] font-medium`}>{initialsOf(displayName(owner))}</AvatarFallback>
                    </Avatar>
                    <span className="truncate">{displayName(owner)}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={podId} onValueChange={onPodChange}>
            <SelectTrigger aria-label="Filter connector activity by team" className="h-9 w-full sm:w-40"><SelectValue placeholder={isManager ? 'My projects' : 'All teams'} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{isManager ? 'My projects' : 'All teams'}</SelectItem>
              {pods.map((pod) => <SelectItem key={pod.id} value={pod.id}>{pod.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <CalendarDays className="hidden h-4 w-4 text-muted-foreground sm:block" />
          <Select value={range} onValueChange={(value) => onRangeChange(value as ActivityRange)}>
            <SelectTrigger aria-label="Connector activity date range" className="h-9 w-full sm:w-[132px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {customRangeLabel && <SelectItem value="custom">{customRangeLabel}</SelectItem>}
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="10">Last 10 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="all">All activity</SelectItem>
            </SelectContent>
          </Select>
          {hasFilters && (
            <Button type="button" variant="ghost" size="sm" onClick={onClearFilters}>
              Clear filters
            </Button>
          )}
        </div>
      </div>

      <Input aria-label="Search connector activity" placeholder="Search connector activity..." value={query} onChange={event => onQueryChange(event.target.value)} className="sm:max-w-sm" />
      {!loading && !error && total > 0 && (
        <p className="text-sm text-muted-foreground">
          Showing {activityRows.length ? `${page * pageSize + 1}–${page * pageSize + activityRows.length}` : '0'} of {total} connector {total === 1 ? 'result' : 'results'}
        </p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading ? (
        <div className="border border-border bg-background p-6 shadow-sm">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="mt-3 h-10 w-full" />
          <Skeleton className="mt-3 h-10 w-full" />
        </div>
      ) : activityRows.length === 0 ? (
        <div className="border border-border bg-background p-10 text-center shadow-sm">
          <p className="font-medium text-foreground">{hasFilters ? 'No connector activity matches these filters.' : 'No connector activity in this range.'}</p>
          <p className="mt-1 text-sm text-muted-foreground">{hasFilters ? 'Try adjusting your search or filters.' : 'Activity will appear here when a connector records a check-in.'}</p>
          {hasFilters && (
            <Button type="button" variant="outline" className="mt-4" onClick={onClearFilters}>
              Clear filters
            </Button>
          )}
        </div>
      ) : (
        <div className={focused ? 'flex flex-col overflow-hidden border border-border bg-background shadow-sm' : 'flex min-h-0 flex-1 flex-col overflow-hidden border border-border bg-background shadow-sm'}>
          <div className={focused ? 'overflow-auto' : 'min-h-0 flex-1 overflow-auto'}>
          <table className="w-full min-w-[760px] table-fixed caption-bottom text-sm">
            <colgroup>
              <col className="w-[12%]" />
              <col className="w-[20%]" />
              <col className="w-[17%]" />
              <col className="w-[43%]" />
              <col className="w-[6%]" />
              <col className="w-12" />
            </colgroup>
            <thead>
              <tr className="border-b border-border">
                <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="latest" label="Last active" sort={sort} onSort={onSort} />
                <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="name" label="Connector" sort={sort} onSort={onSort} />
                <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="owner" label="Owner" sort={sort} onSort={onSort} />
                <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="update" label="Latest update" sort={sort} onSort={onSort} />
                <SortableHeader className="h-11 px-4 text-left align-middle font-medium text-muted-foreground" column="files" label="Files changed" sort={sort} onSort={onSort} />
                <th className="h-11 w-12 px-4 text-right align-middle font-medium text-muted-foreground" aria-label="Details" />
              </tr>
            </thead>
            <tbody>
              {activityRows.map((row) => {
                const agent = agents.find((item) => item.id === row.agent_id)
                const owner = agent ? membersById.get(agent.user_id) : undefined
                const ownerName = row.owner_name || row.owner_email || (owner ? displayName(owner) : 'Unknown owner')
                return (
                  <tr
                    key={row.agent_id}
                    className="cursor-pointer border-b border-border transition-colors hover:bg-muted/50"
                    onClick={() => onOpenAgent(row.agent_id)}
                  >
                    <td className="truncate p-3 align-middle text-muted-foreground">{row.latest_checkin_date ? new Date(`${row.latest_checkin_date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'No activity'}</td>
                    <td className="truncate p-3 align-middle font-medium text-foreground">{row.agent_name || agent?.name || 'Unknown connector'}</td>
                    <td className="truncate p-3 align-middle text-muted-foreground">{ownerName}</td>
                    <td className="overflow-hidden p-3 align-middle text-foreground">
                      <p className="truncate" title={displayWorkSummary(row.latest_task)}>{displayWorkSummary(row.latest_task)}</p>
                      <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                        {activityLabel(row.latest_classification, row.latest_activity_kind)}
                      </p>
                      <ActivitySummaryStatus activity={{ summary_status: row.latest_summary_status,
                        summary_attempts: row.latest_summary_attempts, summary_last_attempt_at: row.latest_summary_last_attempt_at,
                        summary_error: row.latest_summary_error }} />
                      <p className="mt-1 text-xs text-muted-foreground">{row.activity_count} {row.activity_count === 1 ? 'update' : 'updates'} in this range</p>
                    </td>
                    <td className="p-3 align-middle text-muted-foreground">{row.files_changed ?? 0}</td>
                    <td className="p-3 text-right align-middle"><ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
          <div className="flex items-stretch justify-end divide-x divide-border border-t border-border">
            <div className="flex-1" />
            <button
              type="button"
              disabled={page === 0}
              onClick={() => onPageChange(Math.max(0, page - 1))}
              className="flex w-16 items-center justify-center py-[18px] disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <ChevronLeft className={`h-5 w-5 ${page === 0 ? 'text-muted-foreground/40' : 'text-foreground'}`} />
            </button>
            <div className="flex w-40 items-center justify-center py-[18px] text-sm text-muted-foreground">Page {page + 1} of {totalPages}</div>
            <button
              type="button"
              disabled={page + 1 >= totalPages}
              onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
              className="flex w-16 items-center justify-center py-[18px] disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <ChevronRight className={`h-5 w-5 ${page + 1 >= totalPages ? 'text-muted-foreground/40' : 'text-foreground'}`} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default TeamAgents
